import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {DeleteOutlined, PaperClipOutlined, UploadOutlined} from '@ant-design/icons';
import {Button, Form, Grid, Image, Input, Popconfirm, Space, theme, Typography} from 'antd';
import classNames from 'classnames';
import {ImageSkeleton} from 'components/ImageSkeleton';
import {Shell} from 'components/Shell';
import {getIsMobile, isFile} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {GarageDto} from '../../../../../PRMGApiClient';
import {usePrompt} from '../../helpers/useBlocker';
import {GarageState} from '../../store/garage/GarageState';

export const GarageEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {garageId} = useParams();
  const [state] = useState(() => new GarageState());
  const inputRef = useRef<HTMLInputElement>(null);

  const screen = Grid.useBreakpoint();
  const {token} = theme.useToken();

  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const isDirty = useMemo(() => {
    if (!garageId || !formValues) return false;
    return !Object.keys(formValues).every((key) => formValues[key] === state.item[key as keyof GarageDto]);
  }, [state.item, garageId, formValues]);

  const imageUrl = useMemo(() => {
    if (state.image) {
      if (isFile(state.image)) {
        return URL.createObjectURL(state.image);
      } else {
        return state.image.url ?? undefined;
      }
    }
    return undefined;
  }, [state.image]);

  const isMobile = useMemo(() => getIsMobile(screen), [screen]);

  useEffect(() => {
    state.loadGarage(parseInt(garageId ?? '0'));
  }, [garageId, state]);

  useEffect(() => {
    form.setFieldsValue(state.item);
  }, [form, state.item]);

  usePrompt(t('Are you sure? There are unsaved changes.'), garageId ? isDirty : false);

  return (
    <>
      <Shell
        title={garageId ? t('Edit Garage') : t('Add Garage')}
        backLink
        actionButtons={[
          <Popconfirm
            key="delete"
            placement="bottomRight"
            title={t('Are you sure you want to remove this garage?')}
            onConfirm={() => state.deleteGarage()}
            okText={t('Yes')}
            cancelText={t('No')}
          >
            <Button size="large" className={classNames('shell-btn', garageId ? 'visible' : 'invisible')}>
              {t('Remove')}
            </Button>
          </Popconfirm>,
          <Button key="save" size="large" type="primary" form="myForm" htmlType="submit" className="shell-btn">
            {t('Save')}
          </Button>,
        ]}
      >
        <Form
          id="myForm"
          form={form}
          onFinish={(payload) => state.save(payload).then((id) => id && appState.updateGarageData(id))}
          layout="vertical"
          className="xl:w-1/2"
        >
          <Form.Item
            required
            label={t('Name')}
            name="name"
            rules={[{required: true, message: String(t('Name is required.'))}]}
          >
            <Input placeholder={t('Garage name')} />
          </Form.Item>
          <Form.Item name="description" label={String(t('Description'))}>
            <Input.TextArea size="middle" placeholder={String(t('Enter description'))} />
          </Form.Item>
          <Form.Item label={t('Photo')}>
            <Space className="items-start" size="middle" direction={isMobile ? 'vertical' : 'horizontal'}>
              <Image
                rootClassName="object-cover rounded-lg h-64 w-64"
                style={{height: '100%', width: '100%'}}
                className="object-cover rounded-lg items-start"
                src={imageUrl}
                placeholder={<ImageSkeleton />}
                preview={!!state.image}
              />
              <Space direction="vertical">
                <span tabIndex={0} role="button">
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    style={{display: 'none'}}
                    onChange={(e) => state.setImage(e.target.files?.[0] as File)}
                  />
                  <Button disabled={!!state.image} icon={<UploadOutlined />} onClick={() => inputRef.current?.click()}>
                    {t('Click to Upload')}
                  </Button>
                </span>
                {state.image && (
                  <div className="flex items-center w-full gap-2">
                    <PaperClipOutlined style={{color: token.colorIcon}} />
                    <Typography.Text>{isFile(state.image) ? state.image.name : state.image.fileName}</Typography.Text>
                    <Button
                      className="ml-auto"
                      size="small"
                      type="text"
                      icon={<DeleteOutlined style={{fontSize: token.fontSizeIcon}} />}
                      onClick={() => state.removeImage()}
                    />
                  </div>
                )}
              </Space>
            </Space>
          </Form.Item>
        </Form>
      </Shell>
    </>
  );
});
